import * as React from 'react';
import { useEffect, useState } from 'react';
import LoadingScreen from '../../components/muiLegacy/LoadingScreen/LoadingScreen';
import VoxelAlert from '../../components/voxel/Alert/VoxelAlert';
import VoxelPageContainer from '../../components/voxel/Containers/VoxelPageContainer';
import { ProjectGq } from '../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../graphql/projects/projectsApiService';
import { CurrentUserGq } from '../../graphql/users/models/CurrentUserGq';
import useIsScrolling from '../../logic/otherUtilities/useIsScrolling';
import ProjectAppBar from '../ProjectPage/ProjectAppBar/ProjectAppBar';
import { useProjectPageTitle } from '../ProjectPage/useProjectPageTitle';
import AnalysisDrawer from './AnalysisDrawer/AnalysisDrawer';
import { createAnalysisList } from './AnalysisList';
import AnalysisTopSection from './AnalysisTopSection';
import ChartContainer from './ChartContainer';
import { useAnalysisFilterState } from './Filter/useAnalysisFilterState';
import { parsePalette } from './Palette/ColorPalette';
import { useAnalysisNavigation } from './useAnalysisNavigation';
import { FrontendUserEventGq } from '../../graphql/users/queries/useFrontendLogUserEventGq';
import { usersApiService } from '../../graphql/users/usersApiService';

export interface AnalysisPageProps {
  project: ProjectGq;
  reload: () => Promise<any>;
  path: string;
  user: CurrentUserGq;
}

const AnalysisPage: React.FC<AnalysisPageProps> = ({ project, reload, path, user }) => {
  useProjectPageTitle('Analyse', project);
  const [openDrawer, setOpenDrawer] = useState(true);

  // Log user event
  const logUserEventMutation = usersApiService.mutations.useLogFrontendUserEvent();
  useEffect(() => {
    logUserEventMutation({
      event: FrontendUserEventGq.VISIT_PROJECT_ANALYSIS_PAGE,
      projectId: project.id,
    });
  }, [project.id]);

  // Data fetching
  const domainTrafficQuery = projectsApiService.queries.analysis.useGetDomainTraffic({
    projectId: project.id,
    domainIds: project.domains.map((d) => d.id),
    tagGroupId: -1,
  });

  const { result: analysis, query: analysisQuery } =
    projectsApiService.queries.analysis.useGetAvailableAnalysisData({
      projectId: project.id,
      serpOnly: false,
    });

  // Palette
  const { data: palettes, query: paletteQuery } =
    projectsApiService.queries.analysis.useGetAnalysisColorPalettes();
  const palette = React.useMemo(
    () => parsePalette(palettes?.find((p) => p.id === project.analysisColorPaletteId)?.themeJson),
    [project.analysisColorPaletteId, palettes]
  );

  const localReload = React.useCallback(
    () => Promise.all<unknown>([reload(), paletteQuery.refetch()]),
    [reload, paletteQuery.refetch]
  );

  // Filters
  const { analysisState, setAnalysisState, analysisStateInitialized } = useAnalysisFilterState(
    project,
    domainTrafficQuery?.result?.domainAnalyses
  );
  const language = analysisState.language;
  const slideTheme = analysisState?.theme ?? 'voxel';

  // Analysis list
  const analysisList = createAnalysisList(project, analysis, language);
  const chartList = analysisList
    .map((i) => {
      if (i.chart) {
        return i;
      } else {
        return i.analysisList;
      }
    })
    .flat();

  // Navigation
  const {
    selectedAnalysisId,
    setSelectedAnalysisId,
    getAnalysisUrl,
    canGoNext,
    canGoPrev,
    handleGoToNext,
    handleGoToPrev,
  } = useAnalysisNavigation(chartList, path);

  const isScrolling = useIsScrolling();

  // Rendering
  if ((analysisQuery?.loading && !analysis) || !analysisStateInitialized || !palettes) {
    return (
      <>
        <ProjectAppBar project={project} user={user} />
        <LoadingScreen />
      </>
    );
  }

  if (!analysis.keywordData) {
    return (
      <>
        <ProjectAppBar project={project} user={user} />
        <VoxelPageContainer>
          <VoxelAlert severity={'warning'} title="No keywords found">
            Add keywords before analyzing project.
          </VoxelAlert>
        </VoxelPageContainer>
      </>
    );
  }

  // Chart selection
  const selectedChart = chartList.find((x) => x.downloadId === selectedAnalysisId);
  const ChartComponent = selectedChart?.chart;

  return (
    <>
      <ProjectAppBar project={project} user={user} isScrolling={isScrolling} />
      <div className={'h-14'} /> {/* ProjectAppBar size */}
      <div className="fixed w-full h-full flex flex-cols overflow-y-scroll">
        <AnalysisDrawer
          analysisList={analysisList}
          openDrawer={openDrawer}
          setOpenDrawer={setOpenDrawer}
          selectedAnalysisId={selectedAnalysisId}
          setSelectedAnalysisId={setSelectedAnalysisId}
          getAnalysisUrl={getAnalysisUrl}
        />
        <div className="container mx-auto p-8 mt-0">
          <div className="flex flex-col gap-2 justify-center items-center mx-auto lg:max-w-6xl min-w-min pb-4">
            <AnalysisTopSection
              project={project}
              language={language}
              slideTheme={slideTheme}
              canGoNext={canGoNext}
              canGoPrev={canGoPrev}
              handleGoToNext={handleGoToNext}
              handleGoToPrev={handleGoToPrev}
              analysisState={analysisState}
              setAnalysisState={setAnalysisState}
              setSelectedAnalysisId={setSelectedAnalysisId}
              selectedChart={selectedChart}
              charts={chartList}
              reload={localReload}
              palettes={palettes}
            />
            {ChartComponent && (
              <ChartContainer
                chart={selectedChart}
                slideTheme={slideTheme}
                language={language}
                project={project}
                analysisFilterState={analysisState}
                setAnalysisFilterState={setAnalysisState}
                palette={palette}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AnalysisPage;
